import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { PhoneWrapper } from 'src/svg/phone-wrapper';

import { CuraleafFonts, JarsFonts } from 'shared/utils/customize';
import { usePreview } from './use-preview';
import { PreviewData } from './preview.types';
import TabBar from './mobile/mobile-tab-bar';
import MobileHeader from './mobile/mobile-header';

export type PreviewProps = {
  asMobileApp?: boolean;
  children: React.ReactNode;
  data: PreviewData;
};

export function Preview({ asMobileApp, children, data }: PreviewProps): JSX.Element {
  const { theme, encodedFonts } = usePreview(data);
  const flags = useFlags();
  const isCuraleafFontsEnabled = flags['growth.ecomm.curaleaf-fonts.entitlement'] ?? false;
  const isJarsFontsEnabled = flags['growth.ecomm.jars-fonts.entitlement'] ?? false;
  const showMobileRedesign = flags['ecomm.app.v2-redesign'] ?? false;

  return (
    <ThemeProvider theme={theme}>
      {isCuraleafFontsEnabled && <CuraleafFonts />}
      {isJarsFontsEnabled && <JarsFonts />}

      <Container>
        {encodedFonts && (
          <link href={`https://fonts.googleapis.com/css2?${encodedFonts}&display=swap`} rel='stylesheet' />
        )}

        <PhoneWrapper />

        <ContentContainer $backgroundColor={asMobileApp ?? false ? data.mobile?.backgroundColor : null}>
          {!showMobileRedesign && asMobileApp && (
            <MobileHeader
              backgroundColor={data.mobile?.headerBackgroundColor}
              logo={data.mobile?.headerImageUrl}
              navigationStyle={data.mobile?.headerNavigationStyle}
            />
          )}
          {!asMobileApp && <PreviewBanner>Preview</PreviewBanner>}
          <ScrollableContainer $asMobileApp={asMobileApp ?? false} $showMobileRedesign={showMobileRedesign}>
            {showMobileRedesign && asMobileApp && (
              <MobileHeader
                backgroundColor={data.mobile?.headerBackgroundColor}
                logo={data.mobile?.headerImageUrlV2}
                navigationStyle={data.mobile?.headerNavigationStyle}
              />
            )}
            {children}
          </ScrollableContainer>
          {asMobileApp && <TabBar mobileCustomization={data.mobile} />}
        </ContentContainer>
      </Container>
    </ThemeProvider>
  );
}

const Container = styled.div`
  height: 802px;
  max-width: 370px;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const ScrollableContainer = styled.div<{ $asMobileApp: boolean; $showMobileRedesign: boolean }>`
  font-family: ${({ theme }) => theme.primaryFont};
  height: ${({ $asMobileApp, $showMobileRedesign }) => (!$asMobileApp || $showMobileRedesign ? '720px' : '620px')};
  margin-top: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const ContentContainer = styled.div<{ $backgroundColor?: string | null }>`
  background-color: ${({ $backgroundColor }) => $backgroundColor ?? '#FFFFFF'};
  border-radius: 44px;
  bottom: 12px;
  left: 11px;
  overflow: hidden;
  position: absolute;
  right: 11px;
  top: 11px;
  // Old school fix for Safari not respecting border radius clipping
  transform: translate3d(0, 0, 0);

  & a {
    color: ${({ theme }) => theme.linkColor};
  }
`;

const PreviewBanner = styled.div`
  font-size: 12px;
  font-weight: 700;
  height: 60px;
  line-height: ${17 / 12};
  padding: 33px 0 0;
  text-align: center;
  text-transform: uppercase;
`;
